import { Bell, Search, LogOut, User, Check, MailPlus, Eye, Loader2, RefreshCw } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { OfflineQueueIndicator } from "@/components/OfflineQueueIndicator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, AppRole } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";

const titleCase = (s: string) =>
  s.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const roleLabels: Record<string, string> = {
  superadmin: "Super Admin",
  principal: "Principal",
  deputy_principal: "Deputy Principal",
  head_admission: "Head of Admission",
  head_administration: "Head of Administration",
  head_academic: "Head of Academic",
  head_examination: "Head of Examination",
  head_accounting: "Head of Accounting",
  head_student_service: "Head of Student Services",
  head_discipline: "Head of Discipline",
  hod_cmt: "HoD CMT",
  hod_pst: "HoD PST",
  hod_sw: "HoD SW",
  it_incharge: "IT Incharge",
  security_man: "Security Man",
  cr_cmt4: "CR CMT4",
  cr_cmt5: "CR CMT5",
  cr_cmt6: "CR CMT6",
  cr_pst4: "CR PST4",
  cr_pst5: "CR PST5",
  cr_pst6: "CR PST6",
  cr_sw4: "CR SW4",
  cr_sw5: "CR SW5",
  cr_sw6: "CR SW6",
  auditor: "Auditor",
  student: "Student",
};

const previewableRoles: AppRole[] = [
  "principal", "deputy_principal",
  "head_admission", "head_administration", "head_academic",
  "head_examination", "head_accounting", "head_student_service",
  "head_discipline", "hod_cmt", "hod_pst", "hod_sw", "it_incharge",
];

export function TopBar() {
  const { user, roles, realRoles, signOut, isImpersonating } = useAuth();
  const impersonation = useImpersonation();
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const primaryRole = roles[0];
  const isSuperadmin = realRoles.includes("superadmin");
  const isStudent = roles.length === 1 && roles[0] === "student";

  const [impDialogOpen, setImpDialogOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Click-outside handler for search
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Student search query
  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ["student-search", searchQuery],
    queryFn: async () => {
      const q = searchQuery.trim();
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, form_iv_index, program, class, nactevet_reg_number")
        .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,student_number.ilike.%${q}%,form_iv_index.ilike.%${q}%,nactevet_reg_number.ilike.%${q}%`)
        .limit(10);
      return data || [];
    },
    enabled: searchQuery.trim().length >= 2 && !isStudent,
    staleTime: 30 * 1000,
  });

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("user_id", user!.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: staffUsers } = useQuery({
    queryKey: ["staff-users-for-impersonation"],
    queryFn: async () => {
      const { data: allRoles } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .neq("role", "student");

      if (!allRoles) return [];

      const userIds = [...new Set(allRoles.map((r) => r.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p.full_name]) || []);
      const roleMap = new Map<string, AppRole[]>();
      allRoles.forEach((r) => {
        const existing = roleMap.get(r.user_id) || [];
        existing.push(r.role as AppRole);
        roleMap.set(r.user_id, existing);
      });

      return userIds
        .filter((id) => id !== user?.id)
        .map((id) => ({
          id,
          fullName: profileMap.get(id) || "Unknown",
          roles: roleMap.get(id) || [],
        }));
    },
    enabled: isSuperadmin && impDialogOpen,
    staleTime: 2 * 60 * 1000,
  });

  const displayName =
    isImpersonating && impersonation.targetUser
      ? impersonation.targetUser.fullName
      : profile?.full_name || user?.user_metadata?.full_name || user?.email?.split("@")[0];

  const timeAgo = (t: string) => {
    const diff = Date.now() - new Date(t).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const filteredStaff = staffUsers?.filter(
    (s) =>
      s.fullName.toLowerCase().includes(staffSearch.toLowerCase()) ||
      s.roles.some((r) => (roleLabels[r] || r).toLowerCase().includes(staffSearch.toLowerCase()))
  );

  return (
    <>
      <header className="h-14 flex items-center justify-between border-b bg-card px-2 sm:px-4 gap-2 sm:gap-4">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
          <SidebarTrigger />
          {!isStudent && (
            <div className="relative hidden md:block" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
              <Input
                placeholder="Search students by name, index, reg no..."
                className="pl-9 w-72 h-9 bg-muted/50"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
                onKeyDown={(e) => { if (e.key === "Escape") setShowResults(false); }}
              />
              {showResults && searchQuery.trim().length >= 2 && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-popover border rounded-md shadow-lg z-50 overflow-hidden">
                  {!searchResults || searchResults.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">
                      {isSearching ? "Searching..." : "No students found"}
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[320px]">
                      {searchResults.map((s: any) => (
                        <button
                          key={s.id}
                          className="w-full text-left px-3 py-2 hover:bg-accent transition-colors border-b last:border-b-0"
                          onClick={() => {
                            setShowResults(false);
                            setSearchQuery("");
                            navigate(`/administration?search=${encodeURIComponent(s.student_number || s.first_name)}`);
                          }}
                        >
                          <p className="text-sm font-medium">
                            {s.first_name} {s.last_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            {s.student_number && <span>{s.student_number}</span>}
                            {s.form_iv_index && <span>• {s.form_iv_index}</span>}
                            {s.program && <span>• {s.program}</span>}
                            {s.class && <span>• {s.class}</span>}
                          </div>
                        </button>
                      ))}
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2">
          <OfflineQueueIndicator />

          {/* Mobile search button */}
          {!isStudent && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-8 w-8"
              onClick={() => {
                const input = document.createElement("input");
                input.remove();
                setSearchQuery("");
                setShowResults(true);
                setMobileSearchOpen(true);
              }}
            >
              <Search className="h-4 w-4" />
            </Button>
          )}

          {/* Impersonation button - only for real superadmins */}
          {isSuperadmin && !isImpersonating && (
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setImpDialogOpen(true)}
              title="Role Preview / Impersonate"
            >
              <Eye className="h-5 w-5" />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                Notifications
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-auto py-0 text-xs" onClick={() => markAllRead.mutate()}>
                    Mark all read
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ScrollArea className="h-[300px]">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
                ) : (
                  notifications.slice(0, 20).map((n: any) => (
                    <DropdownMenuItem
                      key={n.id}
                      className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!n.read ? "bg-muted/50" : ""}`}
                      onClick={() => {
                        if (!n.read) markAsRead.mutate(n.id);
                        if (n.link) navigate(n.link);
                      }}
                    >
                      <div className="flex items-start gap-2 w-full">
                        {!n.read && <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-tight">{n.title}</p>
                          {n.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>}
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>

          {realRoles.includes("superadmin") && (
            <Button
              variant="ghost"
              size="icon"
              className="relative hidden sm:inline-flex"
              onClick={() => navigate("/user-management?tab=invitations")}
              title="Invitations"
            >
              <MailPlus className="h-5 w-5" />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-9">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium leading-none">{titleCase(displayName || "")}</p>
                  {primaryRole && (
                    <p className="text-xs text-muted-foreground mt-0.5">{roleLabels[primaryRole] || primaryRole}</p>
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-medium">{titleCase(displayName || "")}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {roles.map((r) => (
                    <Badge key={r} variant="secondary" className="text-[10px]">
                      {roleLabels[r] || r}
                    </Badge>
                  ))}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    if ("serviceWorker" in navigator) {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(regs.map((r) => r.unregister()));
                    }
                    if ("caches" in window) {
                      const keys = await caches.keys();
                      await Promise.all(keys.map((k) => caches.delete(k)));
                    }
                  } finally {
                    window.location.reload();
                  }
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Check for updates
              </DropdownMenuItem>
              <DropdownMenuItem onClick={signOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && !isStudent && (
        <div className="md:hidden fixed inset-x-0 top-0 z-50 bg-background border-b p-2 flex items-center gap-2">
          <div className="relative flex-1" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
            <Input
              placeholder="Search students..."
              className="pl-9 h-9 bg-muted/50"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowResults(true);
              }}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Escape") { setMobileSearchOpen(false); setSearchQuery(""); } }}
            />
            {showResults && searchQuery.trim().length >= 2 && (
              <div className="absolute top-full left-0 mt-1 w-full bg-popover border rounded-md shadow-lg z-50 overflow-hidden">
                {!searchResults || searchResults.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    {isSearching ? "Searching..." : "No students found"}
                  </div>
                ) : (
                  <ScrollArea className="max-h-[60vh]">
                    {searchResults.map((s: any) => (
                      <button
                        key={s.id}
                        className="w-full text-left px-3 py-2 hover:bg-accent transition-colors border-b last:border-b-0"
                        onClick={() => {
                          setShowResults(false);
                          setSearchQuery("");
                          setMobileSearchOpen(false);
                          navigate(`/administration?search=${encodeURIComponent(s.student_number || s.first_name)}`);
                        }}
                      >
                        <p className="text-sm font-medium">{s.first_name} {s.last_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {s.student_number && <span>{s.student_number}</span>}
                          {s.program && <span>• {s.program}</span>}
                        </div>
                      </button>
                    ))}
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setMobileSearchOpen(false); setSearchQuery(""); setShowResults(false); }}>
            Cancel
          </Button>
        </div>
      )}

      {/* Impersonation Dialog */}
      <Dialog open={impDialogOpen} onOpenChange={setImpDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Role Preview & Impersonation
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="role">
            <TabsList className="w-full">
              <TabsTrigger value="role" className="flex-1">Quick Role Preview</TabsTrigger>
              <TabsTrigger value="user" className="flex-1">Impersonate User</TabsTrigger>
            </TabsList>
            <TabsContent value="role" className="mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                View the system as any role. Sidebar, dashboard, and modules will render as that role sees them.
              </p>
              <ScrollArea className="h-[300px]">
                <div className="space-y-1">
                  {previewableRoles.map((role) => (
                    <Button
                      key={role}
                      variant="ghost"
                      className="w-full justify-start h-9 text-sm"
                      onClick={() => {
                        impersonation.startRolePreview(role);
                        setImpDialogOpen(false);
                        navigate("/dashboard");
                      }}
                    >
                      {roleLabels[role] || role}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
            <TabsContent value="user" className="mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                See the system exactly as a specific staff member experiences it.
              </p>
              <Input
                placeholder="Search by name or role..."
                value={staffSearch}
                onChange={(e) => setStaffSearch(e.target.value)}
                className="mb-3"
              />
              <ScrollArea className="h-[260px]">
                <div className="space-y-1">
                  {filteredStaff?.map((staff) => (
                    <Button
                      key={staff.id}
                      variant="ghost"
                      className="w-full justify-start h-auto py-2 text-sm"
                      onClick={async () => {
                        await impersonation.startUserImpersonation(staff.id);
                        setImpDialogOpen(false);
                        navigate("/dashboard");
                      }}
                    >
                      <div className="text-left">
                        <p className="font-medium">{staff.fullName}</p>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {staff.roles.map((r) => (
                            <Badge key={r} variant="secondary" className="text-[10px]">
                              {roleLabels[r] || r}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Button>
                  ))}
                  {filteredStaff?.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No staff members found</p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
